/**
 * Dev-only perf telemetry. Gated by NEXT_PUBLIC_PERF_INSTRUMENTATION === '1'
 * (or any non-production NODE_ENV) so it never runs in a prod bundle.
 *
 * Counters are stored in-memory and exposed on `window.__perf` for quick
 * inspection from the DevTools console:
 *
 *   __perf.rpc()       - RPC requests grouped by JSON-RPC method
 *   __perf.queries()   - React Query fetches grouped by query key
 *   __perf.report()    - printable summary (table form)
 *   __perf.reset()     - clear counters
 */

export interface RpcStat {
  count: number
  totalMs: number
  errors: number
}

export interface QueryStat {
  count: number
  lastDurationMs?: number
  totalMs: number
  errors: number
}

const rpcCounters = new Map<string, RpcStat>()
const queryCounters = new Map<string, QueryStat>()

export function isPerfEnabled(): boolean {
  if (typeof window === 'undefined') return false
  // Never instrument under test runners — mocked query clients / transports
  // don't expose the expected APIs, and the counters would just add noise.
  if (process.env.NODE_ENV === 'test') return false
  if (process.env.NEXT_PUBLIC_PERF_INSTRUMENTATION === '1') return true
  return process.env.NODE_ENV !== 'production'
}

export function recordRpc(method: string, durationMs: number, ok: boolean): void {
  const entry = rpcCounters.get(method) ?? { count: 0, totalMs: 0, errors: 0 }
  entry.count += 1
  entry.totalMs += durationMs
  if (!ok) entry.errors += 1
  rpcCounters.set(method, entry)
}

export function recordQuery(key: string, durationMs: number | undefined, ok: boolean): void {
  const entry = queryCounters.get(key) ?? { count: 0, totalMs: 0, errors: 0 }
  entry.count += 1
  if (durationMs !== undefined) {
    entry.lastDurationMs = durationMs
    entry.totalMs += durationMs
  }
  if (!ok) entry.errors += 1
  queryCounters.set(key, entry)
}

function toSortedTable<T extends { count: number }>(map: Map<string, T>, labelKey: string): Record<string, T> {
  const sorted = [...map.entries()].sort((a, b) => b[1].count - a[1].count)
  return Object.fromEntries(sorted.map(([k, v]) => [k || `<${labelKey}>`, v]))
}

function rpc() {
  return toSortedTable(rpcCounters, 'method')
}

function queries() {
  return toSortedTable(queryCounters, 'queryKey')
}

function reset(): void {
  rpcCounters.clear()
  queryCounters.clear()

  if (typeof performance !== 'undefined') {
    try {
      performance.clearMarks()
      performance.clearMeasures()
    } catch {
      // no-op: browser may disallow clearing in some contexts
    }
  }
}

function report(): void {
  const rpcTotal = [...rpcCounters.values()].reduce((s, r) => s + r.count, 0)
  const rpcMs = [...rpcCounters.values()].reduce((s, r) => s + r.totalMs, 0)
  const queryTotal = [...queryCounters.values()].reduce((s, q) => s + q.count, 0)

  console.groupCollapsed(
    `%c[perf] ${rpcTotal} RPC calls (${Math.round(rpcMs)}ms total) · ${queryTotal} query fetches`,
    'color:#9f9',
  )
  // biome-ignore lint/suspicious/noConsoleLog: dev-only perf diagnostics
  console.log('RPC by method:')
  console.table(rpc())
  // biome-ignore lint/suspicious/noConsoleLog: dev-only perf diagnostics
  console.log('Top queries by fetch count:')
  console.table(queries())
  console.groupEnd()
}

export interface PerfApi {
  rpc: typeof rpc
  queries: typeof queries
  reset: typeof reset
  report: typeof report
}

export function installPerfApi(): void {
  if (!isPerfEnabled()) return
  const api: PerfApi = { rpc, queries, reset, report }
  ;(window as unknown as { __perf?: PerfApi }).__perf = api
  console.info('[perf] instrumentation active. Use __perf.report() in the console.')
}
